/**
 * Angular-aware wrapper around {@link ManeuverStateMachine}.
 *
 * Delegates all state-machine logic to the pure class and republishes its
 * state through Angular signals. Tests should target the state-machine
 * module directly; this layer only exists for components/services that
 * need reactive access via DI.
 */

import { Injectable, signal, type Signal, computed } from "@angular/core";

import {
  IDLE_STATE,
  ManeuverStateMachine,
  type ManeuverDeclaration,
  type ManeuverResult,
  type ManeuverSessionPhase,
  type ManeuverSessionState,
  type TickInput,
} from "./maneuver-state-machine";
import type { ManeuverWindow } from "./maneuver-context";

export type {
  ManeuverDeclaration,
  ManeuverResult,
  ManeuverSessionPhase,
  ManeuverSessionState,
  TickInput,
} from "./maneuver-state-machine";

@Injectable({ providedIn: "root" })
export class ManeuverStore {
  private readonly sm = new ManeuverStateMachine();
  private readonly _state = signal<ManeuverSessionState>(IDLE_STATE);

  readonly phase: Signal<ManeuverSessionPhase> = computed(
    () => this._state().phase,
  );
  readonly declaration: Signal<ManeuverDeclaration | null> = computed(
    () => this._state().declaration,
  );
  readonly elapsedSec: Signal<number> = computed(
    () => this._state().elapsedSec,
  );
  readonly window: Signal<ManeuverWindow> = computed(
    () => this._state().window,
  );
  readonly checklist: Signal<ReadonlyMap<string, number>> = computed(
    () => this._state().checklist,
  );
  readonly result: Signal<ManeuverResult | null> = computed(
    () => this._state().result,
  );
  readonly isActive: Signal<boolean> = computed(() => {
    const p = this._state().phase;
    return p === "in_progress" || p === "stabilizing";
  });

  declare(declaration: ManeuverDeclaration): void {
    this.sm.declare(declaration);
    this._state.set(this.sm.state);
  }

  start(nowMs: number): void {
    this.sm.start(nowMs);
    this._state.set(this.sm.state);
  }

  tick(input: TickInput): void {
    this.sm.tick(input);
    this._state.set(this.sm.state);
  }

  abort(): void {
    this.sm.abort();
    this._state.set(this.sm.state);
  }

  reset(): void {
    this.sm.reset();
    this._state.set(this.sm.state);
  }
}
